import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Video, Share2, Zap, ArrowRight } from 'lucide-react';

export default function Home() {
  const navigate = useNavigate();
  const [roomId, setRoomId] = useState('');

  const generateRoomId = () => {
    const id = Math.random().toString(36).substring(2, 12);
    navigate(`/call/${id}`);
  };

  const joinRoom = () => {
    if (roomId.trim()) {
      navigate(`/call/${roomId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
            <Video className="w-5 h-5 text-slate-950" />
          </div>
          <span className="text-xl font-bold text-white">BattleCall</span>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative px-6 py-20 sm:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <span className="text-sm font-medium text-cyan-400">Real-time Video Battles</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight">
            Face-to-Face Competition
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Instantly
            </span>
          </h1>

          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
            Launch a live video battle with anyone. No sign-up. No fees. Just pure real-time connection.
          </p>

          {/* CTA Section */}
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={generateRoomId}
                size="lg"
                className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-bold px-8 h-12 rounded-xl text-lg"
              >
                Start a Battle
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Join Room */}
            <div className="max-w-md mx-auto space-y-3">
              <p className="text-sm text-slate-500">Or join an existing battle</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter room code"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && joinRoom()}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-10"
                />
                <Button
                  onClick={joinRoom}
                  variant="outline"
                  className="border-slate-700 hover:bg-slate-800 h-10"
                >
                  Join
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="px-6 py-20 border-t border-slate-800">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Zap className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="font-bold text-white mb-2">Instant Setup</h3>
            <p className="text-sm text-slate-400">No registration. No installation. Just share a link.</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Video className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="font-bold text-white mb-2">Crystal Clear</h3>
            <p className="text-sm text-slate-400">Peer-to-peer video. Direct connection. No servers in between.</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="font-bold text-white mb-2">Share Instantly</h3>
            <p className="text-sm text-slate-400">Copy the link and invite anyone to battle.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-slate-800 text-center text-sm text-slate-500">
        <p>Built for real-time competition. Privacy-first.</p>
      </footer>
    </div>
  );
}
